import { isIsoDate } from './lossDates';
import { isSkyward, rowLabel } from './lossStaging';

const isBlank = (value) => value == null || value === '';
const MONEY_LABEL = { expense: 'an expense', recovery: 'a recovery' };

export const validateStaging = (displayRows) => {
  const cellErrors = {};
  const messages = [];

  const fail = (row, field, message) => {
    cellErrors[row._key] = [...(cellErrors[row._key] || []), field];
    messages.push(`${rowLabel(row)}: ${message}`);
  };

  displayRows.forEach((row) => {
    // A removed or reverted row sends nothing from its cells, so their values must not block Save.
    if (row._removed || row._reverted) {
      return;
    }
    /* duff binds occurred_at into a NOT NULL column unconditionally on both insert and update, so a
       blank date 500s server-side. Skyward rows are exempt: there a blank is a deliberate clear
       correction, stored as such.
       An unreadable date is not exempt on either source: the Occurred column's setter keeps pasted or
       dragged-in text it cannot read as a date, so the user sees what they typed. Left alone it reaches
       the server as a null (self-reported, another NOT NULL 500) or unparseable text (Skyward, a 400) —
       either way a failure the user cannot read off the row. */
    const touchedDate = row._new || (row._edited || []).includes('occurredAt');
    if (touchedDate) {
      if (!isBlank(row.occurredAt) && !isIsoDate(row.occurredAt)) {
        fail(row, 'occurredAt', 'that occurrence date could not be read.');
      } else if (!isSkyward(row) && isBlank(row.occurredAt)) {
        fail(row, 'occurredAt', 'an occurrence date is required.');
      }
    }
    /* Clearing the cause is reachable on an existing row too, through a range-delete or an empty
       paste. CHECK ((loss_cause_id IS NULL) <> (loss_cause_other IS NULL)) means both blank is a
       server error, so a free-text cause the grid never shows still satisfies the row. A Skyward
       blank is legal for a different reason: it is stored as an active clear correction (shown as
       Unclassified), not a revert to the feed. */
    const touchedCause = row._new || (row._edited || []).includes('lossCause');
    if (touchedCause && !isSkyward(row) && isBlank(row.lossCause) && isBlank(row.lossCauseOther)) {
      fail(row, 'lossCause', 'a loss cause is required.');
    }
    // Only values the user touched are checked; a pre-existing server value must not block a save.
    const touchedIncurred = row._new || (row._edited || []).includes('incurred');
    if (touchedIncurred) {
      if (isBlank(row.incurred)) {
        // insertLossDetails and updateLossDetails bind straight into a NOT NULL column. A Skyward
        // blank is legal: it is stored as an active clear correction ("(cleared)"), not a revert.
        if (!isSkyward(row)) {
          fail(row, 'incurred', 'an indemnity amount is required.');
        }
      } else if (!(Number(row.incurred) >= 0)) {
        fail(row, 'incurred', 'indemnity must be a number of 0 or more.');
      }
    }
    /* Gated on _edited rather than _new: both are NOT NULL columns, and a new row's blank cell is
       resolved to zero by NEW_ROW_DEFAULTS at save time, so only a cell the user actually cleared
       is a problem. On a Skyward row a blank is legal for the same reason as incurred above — it is
       stored as a deliberate clear correction, so all three money fields share one clear semantic. */
    ['expense', 'recovery'].forEach((field) => {
      if (!(row._edited || []).includes(field)) {
        return;
      }
      if (isBlank(row[field])) {
        if (!isSkyward(row)) {
          fail(row, field, `${MONEY_LABEL[field]} amount is required.`);
        }
      } else if (!(Number(row[field]) >= 0)) {
        fail(row, field, `${field} must be a number of 0 or more.`);
      }
    });
  });

  return { valid: messages.length === 0, cellErrors, messages };
};

export const hasCellError = (cellErrors, rowKey, field) => !!cellErrors[rowKey] && cellErrors[rowKey].includes(field);
